import { useEffect, useRef } from "react";

/**
 * Pravidelně volá `callback`, ale jen když je záložka/aplikace na popředí.
 * Optimalizováno pro mobil: v pozadí (zamčený telefon, přepnutá appka) se polling
 * zastaví, aby neujídal baterii ani data. Po návratu do aplikace se `callback`
 * zavolá okamžitě, takže uživatel hned vidí aktuální stav.
 *
 * @param {() => void} callback   Akce, která načte čerstvá data.
 * @param {number} intervalMs     Interval v ms (běží jen na popředí).
 */
export default function useVisiblePolling(callback, intervalMs) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    let intervalId = null;

    const start = () => {
      if (intervalId === null) {
        intervalId = setInterval(() => savedCallback.current(), intervalMs);
      }
    };

    const stop = () => {
      if (intervalId !== null) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        savedCallback.current(); // okamžitý refetch po návratu do aplikace
        start();
      } else {
        stop();
      }
    };

    // Inicializace podle aktuálního stavu viditelnosti.
    if (document.visibilityState === "visible") start();
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      stop();
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [intervalMs]);
}
